import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  FileText, 
  FileJson, 
  Download, 
  ArrowLeft, 
  Loader2,
  CheckCircle2,
  File
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const exportFormats = [
  {
    id: "markdown",
    name: "Markdown",
    description: "Metin tabanlı format, GitHub ve dokümantasyon için ideal",
    icon: FileText,
    extension: ".md"
  },
  {
    id: "json",
    name: "JSON",
    description: "Yapılandırılmış veri formatı, entegrasyonlar için uygun",
    icon: FileJson,
    extension: ".json"
  }
];

export default function ExportPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectRes, analysesRes] = await Promise.all([
          axios.get(`${API}/projects/${projectId}`),
          axios.get(`${API}/projects/${projectId}/analyses`)
        ]);
        setProject(projectRes.data);
        if (analysesRes.data.length > 0) {
          setAnalysis(analysesRes.data[0]);
        }
      } catch (error) {
        toast.error("Veri yüklenirken hata oluştu");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [projectId]);

  const handleExport = async (format) => {
    if (!analysis) {
      toast.error("Dışa aktarılacak analiz bulunamadı");
      return;
    }

    setExporting(format);
    try {
      const response = await axios.get(
        `${API}/analyses/${analysis.id}/export/${format}`,
        { responseType: format === "json" ? "json" : "blob" }
      );

      if (format === "json") {
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: "application/json" });
        downloadBlob(blob, `analysis_${project.name}.json`);
      } else {
        downloadBlob(response.data, `analysis_${project.name}.md`);
      }

      toast.success("Dosya indirildi!");
    } catch (error) {
      toast.error("Dışa aktarma başarısız");
    } finally {
      setExporting(null);
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-8 lg:p-12 max-w-4xl mx-auto space-y-8" data-testid="export-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate(`/project/${projectId}`)}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Analizi Dışa Aktar</h1>
          <p className="text-muted-foreground mt-1">{project?.name}</p>
        </div>
      </div>

      {/* Analysis Status */}
      {analysis ? (
        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <p className="font-semibold">Analiz Hazır</p>
                <p className="text-sm text-muted-foreground">
                  {Object.keys(analysis.sections || {}).length} bölüm analiz edildi
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="glass-card border-destructive/50">
          <CardContent className="p-6">
            <p className="text-destructive">Henüz tamamlanmış bir analiz yok.</p>
          </CardContent>
        </Card>
      )}

      {/* Export Formats */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Format Seçin</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {exportFormats.map((format) => (
            <Card 
              key={format.id}
              className="glass-card card-interactive"
              data-testid={`export-format-${format.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                    <format.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{format.name}</CardTitle>
                    <p className="text-xs text-muted-foreground">{format.extension}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="mb-4">{format.description}</CardDescription>
                <Button
                  onClick={() => handleExport(format.id)}
                  disabled={!analysis || exporting === format.id}
                  className="w-full"
                  data-testid={`download-${format.id}-btn`}
                >
                  {exporting === format.id ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      İndiriliyor...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      İndir
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Info */}
      <Card className="glass-card">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <File className="w-5 h-5 text-muted-foreground mt-0.5" />
            <div>
              <p className="font-medium mb-1">PDF ve Word Desteği</p>
              <p className="text-sm text-muted-foreground">
                Markdown dosyasını indirdikten sonra Pandoc veya benzeri araçlarla 
                PDF ve Word formatlarına dönüştürebilirsiniz.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
